import {
  checkFluencyResponse,
  checkLearningRecord,
  incomingFluencyResponse,
  incomingLearningRecord,
  type IncomingFluencyResponse,
  type IncomingLearningRecord,
} from "@bindery/convex-reader/submission";
import { extractNormalizedWord } from "@bindery/shared/contracts/word";
import type { GenericValidator } from "convex/values";
import type { QuarantineReason } from "@/storage/db";

export interface QueueRejection {
  field: string;
  reason: QuarantineReason;
}

export type PreQueue<T> =
  | { kind: "queue"; value: T }
  | { kind: "drop" }
  | { kind: "quarantine"; rejection: QueueRejection };

// learning.submit's argument validator fails the whole batch on one unknown
// field, so a stray key in one queued row would block the queue for good.
// Stripping to the validator's own fields makes that row impossible.
function stripToValidator(
  value: unknown,
  validator: GenericValidator,
): unknown {
  if (
    validator.kind !== "object" ||
    typeof value !== "object" ||
    value === null
  ) {
    return value;
  }
  const source = value as Record<string, unknown>;
  return Object.fromEntries(
    Object.entries(validator.fields).flatMap(([key, field]) =>
      source[key] === undefined
        ? []
        : [[key, stripToValidator(source[key], field)]],
    ),
  );
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" &&
  value !== null &&
  Object.getPrototypeOf(value) === Object.prototype;

const firstProblem = (problems: readonly (string | null)[]): string | null =>
  problems.find((problem) => problem !== null) ?? null;

/**
 * The path of the first value Convex's argument validator would refuse, or
 * `null`. convex/values exports no validate(), and one refused row fails the
 * whole learning.submit call. A validator kind this does not know fails
 * closed, so a schema change cannot let an unchecked row through.
 */
export function validatorProblem(
  value: unknown,
  validator: GenericValidator,
  path = "",
): string | null {
  const here = path === "" ? "(row)" : path;
  if (value === undefined) {
    return validator.isOptional === "optional" ? null : here;
  }
  switch (validator.kind) {
    case "string":
      return typeof value === "string" ? null : here;
    case "float64":
      return typeof value === "number" ? null : here;
    case "int64":
      return typeof value === "bigint" ? null : here;
    case "boolean":
      return typeof value === "boolean" ? null : here;
    case "null":
      return value === null ? null : here;
    case "any":
      return null;
    case "literal":
      return value === validator.value ? null : here;
    case "union":
      return validator.members.some(
        (member) => validatorProblem(value, member, path) === null,
      )
        ? null
        : here;
    case "array":
      return Array.isArray(value)
        ? firstProblem(
            value.map((item, index) =>
              validatorProblem(item, validator.element, `${here}[${index}]`),
            ),
          )
        : here;
    case "object": {
      if (!isPlainObject(value)) return here;
      const prefix = path === "" ? "" : `${path}.`;
      const unknownKey = Object.keys(value).find(
        (key) => !(key in validator.fields),
      );
      if (unknownKey !== undefined) return `${prefix}${unknownKey}`;
      return firstProblem(
        Object.entries(validator.fields).map(([key, field]) =>
          validatorProblem(value[key], field, `${prefix}${key}`),
        ),
      );
    }
    default:
      return here;
  }
}

export const toWireRecord = (value: unknown): IncomingLearningRecord =>
  stripToValidator(value, incomingLearningRecord) as IncomingLearningRecord;

export const toWireResponse = (value: unknown): IncomingFluencyResponse =>
  stripToValidator(value, incomingFluencyResponse) as IncomingFluencyResponse;

const hasNoLetters = (word: string): boolean =>
  extractNormalizedWord(word) === "";

const invalidArgument = (field: string): PreQueue<never> => ({
  kind: "quarantine",
  rejection: { field, reason: "invalid_argument" },
});

// The server's own validator and checks, run before a row is written: a row
// it would refuse goes to quarantine here, so client and server cannot drift.
export function preQueueRecord(
  record: IncomingLearningRecord,
  readerId: string,
  now: number,
): PreQueue<IncomingLearningRecord> {
  const value = toWireRecord(record);
  const problem = validatorProblem(value, incomingLearningRecord);
  if (problem !== null) return invalidArgument(problem);
  if (hasNoLetters(value.word)) return { kind: "drop" };
  const checked = checkLearningRecord(value, readerId, now);
  return checked.ok
    ? { kind: "queue", value }
    : { kind: "quarantine", rejection: checked.rejection };
}

export function preQueueResponse(
  response: IncomingFluencyResponse,
  readerId: string,
  now: number,
): PreQueue<IncomingFluencyResponse> {
  const stripped = toWireResponse(response);
  const problem = validatorProblem(stripped, incomingFluencyResponse);
  if (problem !== null) return invalidArgument(problem);
  if (hasNoLetters(stripped.word)) return { kind: "drop" };
  const checked = checkFluencyResponse(stripped, readerId, now);
  if (!checked.ok) return { kind: "quarantine", rejection: checked.rejection };
  return {
    kind: "queue",
    value: { ...stripped, word: extractNormalizedWord(stripped.word) },
  };
}
